import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap, tap } from 'rxjs';
import { HttpHelisaClientService } from '../query/service/http-helisa-client.service';
import { environment } from '../../../../environments/environment';
import { LocalStorageKeys } from '../../utils/local-storage-keys';
import { LoginResponse } from './dto/login.response';
import { TreeProfileView } from '../../components/menu/dto/tree-profile.view';
import { HelisaQueryPostgreSqlService } from '../query/service/helisa-query.postgre-sql.service';
import { ModuleObject } from '../../components/menu/dto/module.object';
import { UserCompanyProfileViewDto } from '../../components/main-menu/model/user-company-profile.view.dto';

export interface AuthorizationPermissions {
  [key: string]: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private urlApiProfile: string = '/api/profiles';
  private readonly url: string = '/api/users';
  httpHelisaClient: HttpHelisaClientService = inject(HttpHelisaClientService);
  private helisaQueryPostgresqlService: HelisaQueryPostgreSqlService = inject(
    HelisaQueryPostgreSqlService
  );
  private urlApiUserCompanyProfiles: string = '/api/user-company-profiles';

  refreshToken(refreshToken: string, authCompany: string) {
    return this.httpHelisaClient
      .post<LoginResponse>(
        environment.authenticationService,
        this.url + '/refresh-token',
        { refreshToken }
      )
      .pipe(
        tap((response: LoginResponse) => {
          localStorage.setItem(
            LocalStorageKeys.credentialsKey,
            response.access_token
          );
          localStorage.setItem(LocalStorageKeys.refreshTokenKey, refreshToken);
          localStorage.setItem(LocalStorageKeys.helisaCompanyKey, authCompany);
        })
      );
  }

  public loadProfile(): Observable<AuthorizationPermissions> {
    return this.httpHelisaClient
      .get<any>(
        environment.authWebService,
        '/api/profiles/load/logged?reload-cache=' + false
      )
      .pipe(
        map(
          (response: any): AuthorizationPermissions =>
            response as AuthorizationPermissions
        )
      );
  }

  getModulesByIdProfile(idProfile: string): Observable<TreeProfileView> {
    return this.helisaQueryPostgresqlService.getServiceWithAuth<TreeProfileView>(
      environment.authWebService,
      this.urlApiProfile + '/' + idProfile + '?reload-cache=true'
    );
  }

  isLogin() {
    if (
      localStorage.getItem(LocalStorageKeys.credentialsKey) ||
      sessionStorage.getItem(LocalStorageKeys.credentialsKey)
    ) {
      return true;
    }
    return false;
  }

  getToken() {
    const savedCredentials = this.getStorageInfo();
    return savedCredentials;
  }

  private getStorageInfo(): string {
    const savedCredentials =
      sessionStorage.getItem(LocalStorageKeys.credentialsKey) ||
      localStorage.getItem(LocalStorageKeys.credentialsKey);
    return savedCredentials as string;
  }

  getModulesList(): Observable<ModuleObject[]> {
    return this.getUserCompanyProfileByIdCompanyAndIdUser().pipe(
      switchMap(
        (
          userCompanyProfile: UserCompanyProfileViewDto
        ): Observable<ModuleObject[]> => {
          return this.getModulesByProfile(
            userCompanyProfile.profile?.id as string
          );
        }
      )
    );
  }

  getModulesByProfile(profileId: string): Observable<ModuleObject[]> {
    return this.getModulesByIdProfile(profileId).pipe(
      map((treeProfile: TreeProfileView): ModuleObject[] => {
        return treeProfile.modules;
      })
    );
  }

  getUserCompanyProfileByIdCompanyAndIdUser(): Observable<UserCompanyProfileViewDto> {
    const idCompany: string | null = localStorage.getItem(
      LocalStorageKeys.helisaCompanyKey
    );
    return this.helisaQueryPostgresqlService.getServiceWithAuth<UserCompanyProfileViewDto>(
      environment.authWebService,
      this.urlApiUserCompanyProfiles + '/logged/company/' + idCompany
    );
  }
}
